<template>
  <div v-if="loaded">
    <ConfirmIFrame
      v-if="shouldDisplayConfirmation"
      :force-error="hasError"
    />
  </div>
</template>

<script lang="ts">
import LoadingSpinner from '~/core/components/shared/LoadingSpinner.vue'
import ConfirmIFrame from '~/core/components/legacy/ConfirmIFrame.vue'

import HeadMetaBuilder from '~/core/ts/util/HeadMetaBuilder'

import { translateByKey, translateForLang } from '~/core/ts/util/translate'
import { capitalizeAllWords } from '~/core/ts/util/string'
import type { SpaMiniLandingInfo } from '~/core/ts/entity/Spa'
import { supportedLangs } from '~/core/ts/lang-config'

import { useFetchProxy } from '~/core/composable/shared/useFetchProxy'
import { useLangSelector } from '~/core/composable/shared/useLangSelector'

definePageMeta({
  layout: 'checkout',
})

export default defineNuxtComponent({
  components: {
    LoadingSpinner,
    ConfirmIFrame,
  },
  async setup() {
    const builder = HeadMetaBuilder.create()

    const title = capitalizeAllWords(translateByKey('online-booking'))

    builder.withTitleAndMetaTitle(`${title} | Spalopia.com`)

    useHead(builder.get())

    const runtime = useRuntimeConfig()
    const route = useRoute()

    const loaded = ref(false)
    const hasError = ref(false)
    const shouldDisplayConfirmation = ref(false)

    const { currentLang } = useLangSelector()

    function decorateSpaURL(uri: string): string {
      return `${runtime.public.domain}${uri}?make-review=1`
    }

    function decorateAlternateByLangURL(uri: string, parameters: string): string {
      return `${runtime.public.domain}${uri}?${parameters}`
    }

    onMounted(() => {
      const utbPath = decodeURIComponent(`${route.query.utb_path}`)
      if (utbPath === 'make-review') {
        const spaUUID = route.query.utb_center_uuid as string

        const posibleLanding = spaLandings[spaUUID]
        if (!posibleLanding) {
          hasError.value = true
          shouldDisplayConfirmation.value = true
          return
        }

        const finalURL = decorateSpaURL(posibleLanding.uri)
        location.href = finalURL
        return
      }

      const { utb_lang } = route.query

      const queryLang = utb_lang as string | undefined

      if (queryLang && currentLang !== queryLang && supportedLangs.includes(queryLang)) {
        const targetURL = translateForLang(runtime.public.urls.static.bookingConfirmationByLang, queryLang)
        const queryBuilder = new URLSearchParams()

        Object.entries(route.query).forEach(([key, value]) => {
          queryBuilder.append(key, value as string)
        })

        const finalURL = decorateAlternateByLangURL(targetURL, queryBuilder.toString())
        location.href = finalURL
        return
      }

      shouldDisplayConfirmation.value = true
      loaded.value = true
    })

    const spaLandings = await useFetchProxy<Record<string, SpaMiniLandingInfo>>(
      '/api/spas/spa-mini-landing-info-dict-by-uuid', {}
    )

    return {
      loaded,
      hasError,
      shouldDisplayConfirmation,
    }
  }
})
</script>

<style lang="scss" scoped>
.contact-form {
  @apply my-4;
  @apply px-6;

  @apply bg-white;
}

#voucher-hbspt-form-target {
  @apply pt-4;
  @apply pb-4;

  @apply overflow-visible;
}
</style>
